import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const auth = {
  registerCandidate: async (userData, candidateData) => {
    const response = await api.post('/auth/register/candidate', {
      email: userData.email,
      password: userData.password,
      user_type: 'candidate',
      ...candidateData
    });
    return response.data;
  },
  
  registerEmployer: async (userData, employerData) => {
    const response = await api.post('/auth/register/employer', {
      email: userData.email,
      password: userData.password,
      user_type: 'employer',
      ...employerData
    });
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Candidate API
export const candidate = {
  getProfile: async () => {
    const response = await api.get('/candidates/me');
    return response.data;
  },
  
  updateProfile: async (data) => {
    const response = await api.put('/candidates/me', data);
    return response.data;
  },
  
  uploadCV: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/candidates/upload-cv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  uploadDocument: async (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/candidates/upload-document?document_type=${documentType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  completeVideoInterview: async (videoData) => {
    const response = await api.post('/candidates/video-interview', videoData);
    return response.data;
  },
  
  getJobMatches: async () => {
    const response = await api.get('/candidates/job-matches');
    return response.data;
  },
  
  getRelocationChecklist: async () => {
    const response = await api.get('/candidates/relocation-checklist');
    return response.data;
  },
  
  updateChecklistTask: async (taskId, status) => {
    const response = await api.put(`/candidates/relocation-checklist/${taskId}?status=${status}`);
    return response.data;
  },
  
  getNotifications: async () => {
    const response = await api.get('/candidates/notifications');
    return response.data;
  },
  
  markNotificationRead: async (notificationId) => {
    const response = await api.put(`/candidates/notifications/${notificationId}/read`);
    return response.data;
  },
  
  getStats: async () => {
    const response = await api.get('/candidates/stats');
    return response.data;
  }
};

// Employer API
export const employer = {
  getProfile: async () => {
    const response = await api.get('/employers/me');
    return response.data;
  },
  
  updateProfile: async (data) => {
    const response = await api.put('/employers/me', data);
    return response.data;
  },
  
  createJob: async (jobData) => {
    const response = await api.post('/employers/jobs', jobData);
    return response.data;
  },
  
  getJobs: async () => {
    const response = await api.get('/employers/jobs');
    return response.data;
  },
  
  getJob: async (jobId) => {
    const response = await api.get(`/employers/jobs/${jobId}`);
    return response.data;
  },
  
  updateJob: async (jobId, data) => {
    const response = await api.put(`/employers/jobs/${jobId}`, data);
    return response.data;
  },
  
  deleteJob: async (jobId) => {
    const response = await api.delete(`/employers/jobs/${jobId}`);
    return response.data;
  },
  
  getCandidates: async () => {
    const response = await api.get('/employers/candidates');
    return response.data;
  },
  
  getCandidate: async (candidateId) => {
    const response = await api.get(`/employers/candidates/${candidateId}`);
    return response.data;
  },
  
  getStats: async () => {
    const response = await api.get('/employers/stats');
    return response.data;
  },
  
  getAnalytics: async () => {
    const response = await api.get('/employers/analytics');
    return response.data;
  }
};

// Job API
export const jobs = {
  getAll: async () => {
    const response = await api.get('/jobs');
    return response.data;
  },
  
  getById: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },
  
  apply: async (jobId) => {
    const response = await api.post(`/jobs/${jobId}/apply`);
    return response.data;
  }
};

// Interview API
export const interviews = {
  create: async (interviewData) => {
    const response = await api.post('/interviews', interviewData);
    return response.data;
  },
  
  getAll: async () => {
    const response = await api.get('/interviews');
    return response.data;
  },
  
  getById: async (interviewId) => {
    const response = await api.get(`/interviews/${interviewId}`);
    return response.data;
  },
  
  update: async (interviewId, data) => {
    const response = await api.put(`/interviews/${interviewId}`, data);
    return response.data;
  },
  
  cancel: async (interviewId) => {
    const response = await api.delete(`/interviews/${interviewId}`);
    return response.data;
  }
};

// Verification API
export const verification = {
  getRequirements: async () => {
    const response = await api.get('/verification/requirements');
    return response.data;
  },
  
  getStatus: async () => {
    const response = await api.get('/verification/status');
    return response.data;
  },
  
  submitDocument: async (documentType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/verification/submit-document?document_type=${documentType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  getCertificate: async () => {
    const response = await api.get('/verification/certificate');
    return response.data;
  },
  
  getCandidateSummary: async (candidateId) => {
    const response = await api.get(`/verification/candidate/${candidateId}/summary`);
    return response.data;
  }
};

export default api;
